import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Shield, FileWarning } from "lucide-react";
import { analysesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function LegalAnalysis() {
  const { projectId } = useParams();
  const [legal, setLegal] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Try to fetch cached
    analysesApi.legal(projectId).then(setLegal).catch(() => {});
  }, [projectId]);

  const run = async () => {
    setLoading(true);
    try {
      const r = await analysesApi.legal(projectId);
      setLegal(r);
      toast.success("Analyse juridique terminée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto w-full" data-testid="legal-page">
      <div className="label-mono text-[10px] text-muted-foreground">MODULE 2.5</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Analyse juridique</h1>

      <div className="mt-4 flex items-start gap-3 border-l-4 border-alert bg-alert/5 p-4 rounded-sm" data-testid="legal-banner">
        <FileWarning className="text-alert shrink-0" />
        <p className="text-xs">
          <strong>DOCUMENT PÉDAGOGIQUE</strong> — Cette analyse est un outil d'aide à la réflexion juridique.
          Elle ne constitue pas un avis juridique et n'a aucune valeur juridictionnelle.
          Référentiel : Principes CNUCED sur la dette souveraine · Principes Directeurs ONU Bohoslavsky.
        </p>
      </div>

      {!legal ? (
        <div className="mt-8 border border-dashed border-border rounded-sm p-10 text-center">
          <Shield size={36} className="mx-auto text-marine dark:text-gold" />
          <p className="mt-3 text-sm text-muted-foreground">Lancez l'analyse juridique (1 appel GPT-4o, caché en base).</p>
          <Button onClick={run} disabled={loading} className="mt-4 bg-marine hover:bg-marine/90 text-white">
            {loading ? "Analyse…" : "Lancer l'analyse juridique"}
          </Button>
        </div>
      ) : (
        <div className="mt-8 space-y-5">
          <div className="grid md:grid-cols-3 gap-3">
            <Chip label="Conformité CNUCED" value={legal.cnuced_compliance} />
            <Chip label="Impact droits humains" value={legal.human_rights_impact} />
            <Chip label="Score risque souverain" value={`${legal.sovereign_risk_score}/100`} />
          </div>
          <div className="border border-border bg-card rounded-sm p-5">
            <h3 className="font-serif font-bold">Évaluation d'ensemble</h3>
            <p className="mt-2 text-sm leading-relaxed">{legal.overall_assessment}</p>
          </div>
          {legal.legal_risks?.length > 0 && (
            <div>
              <h3 className="font-serif font-bold text-xl">Risques juridiques</h3>
              <div className="mt-3 space-y-3">
                {legal.legal_risks.map((r, i) => (
                  <div key={i} className={`border-l-4 pl-4 py-2 border-border ${r.severity === "critique" ? "border-alert" : r.severity === "eleve" ? "border-warn" : ""}`}>
                    <div className="flex gap-2">
                      <strong>{r.risk_type}</strong>
                      <span className="label-mono text-[9px] px-2 rounded-sm bg-muted">{r.severity?.toUpperCase()}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{r.legal_basis}</div>
                    <div className="mt-1 text-sm italic">{r.recommendation}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="label-mono text-[9px] text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-base">{value || "—"}</div>
    </div>
  );
}
